// ===================================
// TRENCH QUEST - Info Tooltip Component
// Provides contextual help and information
// ===================================

import { FC, useState, useRef, useEffect } from 'react'

interface Props {
  content: string | React.ReactNode
  title?: string
  position?: 'top' | 'bottom' | 'left' | 'right'
  children: React.ReactNode
  className?: string
}

export const InfoTooltip: FC<Props> = ({
  content,
  title,
  position = 'top',
  children,
  className = '',
}) => {
  const [isVisible, setIsVisible] = useState(false)
  const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0 })
  const triggerRef = useRef<HTMLDivElement>(null)
  const tooltipRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (isVisible && triggerRef.current && tooltipRef.current) {
      const triggerRect = triggerRef.current.getBoundingClientRect()
      const tooltipRect = tooltipRef.current.getBoundingClientRect()
      const padding = 8

      let top = 0
      let left = 0

      switch (position) {
        case 'top':
          top = -tooltipRect.height - padding
          left = (triggerRect.width - tooltipRect.width) / 2
          break
        case 'bottom':
          top = triggerRect.height + padding
          left = (triggerRect.width - tooltipRect.width) / 2
          break
        case 'left':
          top = (triggerRect.height - tooltipRect.height) / 2
          left = -tooltipRect.width - padding
          break
        case 'right':
          top = (triggerRect.height - tooltipRect.height) / 2
          left = triggerRect.width + padding
          break
      }

      // Ensure tooltip stays within viewport
      const viewportWidth = window.innerWidth
      const viewportHeight = window.innerHeight
      const absoluteLeft = triggerRect.left + left
      const absoluteTop = triggerRect.top + top

      if (absoluteLeft < padding) {
        left = -triggerRect.left + padding
      } else if (absoluteLeft + tooltipRect.width > viewportWidth - padding) {
        left = viewportWidth - triggerRect.left - tooltipRect.width - padding
      }

      if (absoluteTop < padding) {
        top = triggerRect.height + padding
      } else if (absoluteTop + tooltipRect.height > viewportHeight - padding) {
        top = -tooltipRect.height - padding
      }

      setTooltipPosition({ top, left })
    }
  }, [isVisible, position])

  return (
    <div
      ref={triggerRef}
      className={`relative inline-flex ${className}`}
      onMouseEnter={() => setIsVisible(true)}
      onMouseLeave={() => setIsVisible(false)}
      onFocus={() => setIsVisible(true)}
      onBlur={() => setIsVisible(false)}
    >
      {children}

      {isVisible && (
        <div
          ref={tooltipRef}
          className="absolute z-50 w-64 max-w-sm"
          style={{
            top: tooltipPosition.top,
            left: tooltipPosition.left,
          }}
        >
          <div className="bg-game-panel border-2 border-game-border shadow-lg">
            {title && (
              <div className="px-3 py-1.5 bg-game-bg border-b border-game-border">
                <span className="font-pixel text-xs text-game-accent">{title}</span>
              </div>
            )}
            <div className="px-3 py-2">
              {typeof content === 'string' ? (
                <p className="font-retro text-sm text-game-text leading-relaxed">{content}</p>
              ) : (
                content
              )}
            </div>
          </div>
          {/* Arrow */}
          <div
            className={`absolute w-0 h-0 border-solid ${
              position === 'top'
                ? 'bottom-[-8px] left-1/2 -translate-x-1/2 border-l-[8px] border-r-[8px] border-t-[8px] border-l-transparent border-r-transparent border-t-game-border'
                : position === 'bottom'
                ? 'top-[-8px] left-1/2 -translate-x-1/2 border-l-[8px] border-r-[8px] border-b-[8px] border-l-transparent border-r-transparent border-b-game-border'
                : position === 'left'
                ? 'right-[-8px] top-1/2 -translate-y-1/2 border-t-[8px] border-b-[8px] border-l-[8px] border-t-transparent border-b-transparent border-l-game-border'
                : 'left-[-8px] top-1/2 -translate-y-1/2 border-t-[8px] border-b-[8px] border-r-[8px] border-t-transparent border-b-transparent border-r-game-border'
            }`}
          />
        </div>
      )}
    </div>
  )
}

// Simple info icon button with tooltip
interface InfoButtonProps {
  content: string | React.ReactNode
  title?: string
  position?: 'top' | 'bottom' | 'left' | 'right'
  className?: string
}

export const InfoButton: FC<InfoButtonProps> = ({
  content,
  title,
  position = 'top',
  className = '',
}) => {
  return (
    <InfoTooltip content={content} title={title} position={position} className={className}>
      <button
        type="button"
        className="w-5 h-5 rounded-full bg-game-border hover:bg-game-accent/30 flex items-center justify-center transition-colors"
        aria-label="More information"
      >
        <span className="font-pixel text-xs text-game-muted">?</span>
      </button>
    </InfoTooltip>
  )
}

export default InfoTooltip
